import { createFileRoute, useRouter } from "@tanstack/react-router";
import { Check, X } from "lucide-react";
import { useMemo } from "react";
import type { ButtonConfig } from "@/components/common/Complete";
import { JourneyPhaseSuccessPage, SuccessPage } from "@/components/pages/SuccessPage";
import { usersOrganisationsRolesQueryOptions, useSetRoles } from "@/features/auth/api/selectUsersOrganisationsRoles";
import { pollPatientDiaries, diariesByPatientQueryOptions } from "@/features/diaries/api/diaryQueryOptions";
import { useFormsToDo } from "@/features/diaries/features/new/hooks/useFormsToDo";
import { findFirstDueEntryAction } from "@/features/diaries/features/new/services/findFirstDueEntryAction";
import { useAuthCopy } from "@/features/i18n/authCopy";
import { useDiaryCopy } from "@/features/i18n/diaryCopy";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { useJourneyCopy } from "@/features/i18n/journeyCopy";
import { useProfileCopy } from "@/features/i18n/profileCopy";
import { isDiaryQuotaQuickstartError } from "@/features/invite/utils/isDiaryQuotaQuickstartError";
import { useJourneyProgressSummary } from "@/features/onboarding/hooks/useJourneyProgressSummary";
import { usePatientJourney } from "@/features/onboarding/hooks/usePatientJourney";
import { getJourneyPhaseCompleteScreen } from "@/features/onboarding/utils/getNextJourneyPhase";
import {
    getOnboardingContactCompletion,
    getOnboardingPhaseProgress,
} from "@/features/diaries/utils/diaryJourneyProgress";
import { selectProfileByIdQueryOptions } from "@/features/profile/api/selectProfile";
import { getOnboardedAppHome } from "@/features/auth/utils/getAuthenticatedPublicLanding";
import { useAuth } from "@/utils/auth";
import type { StatefulDiaryT } from "@patient-watch/types";
import { supabaseClient } from "@/lib/supabase";
import { useSuspenseQuery } from "@tanstack/react-query";

async function fetchLatestFailedQuickstartError(userId: string): Promise<string | null> {
    const { data, error } = await supabaseClient
        .from("quickstarts")
        .select("error_message")
        .eq("user_id", userId)
        .eq("status", "failed")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

    if (error) {
        console.warn("⚠️ Could not read failed quickstart for quota check:", error.message);
        return null;
    }

    return data?.error_message ?? null;
}

const OnboardingComplete = () => {
    const auth = useAuth();
    const router = useRouter();
    const copy = useProfileCopy();
    const diaryCopy = useDiaryCopy();
    const authCopy = useAuthCopy();
    const journeyCopy = useJourneyCopy();
    const { locale } = useAppLocale();
    const { isPatient, diaries, diaryQuotaBlocked } = Route.useLoaderData();
    const { data: profile } = useSuspenseQuery(selectProfileByIdQueryOptions(auth.id));
    useSetRoles();

    const firstDiary = diaries[0] ?? null;
    const firstDiaryId = firstDiary?.diary_id;
    const hasNoPatientDiaries = isPatient && diaries.length === 0;
    const formsToDo = useFormsToDo(firstDiary?.trackers_view ?? []);
    const journey = usePatientJourney(firstDiary, {
        diaryId: firstDiaryId,
        formsToDo: formsToDo.toDo,
        locale,
        includeFuturePhases: true,
    });
    const journeyProgress = useJourneyProgressSummary(firstDiary, {
        onboardingProgress: getOnboardingPhaseProgress(
            "/onboarding/complete",
            "patient",
            getOnboardingContactCompletion(profile)
        ),
        includeFuturePhases: true,
    });

    const patientJourneyScreen = useMemo(() => {
        if (!firstDiary || !firstDiaryId || !journey.nextStage) return null;
        const nextDueEntryAction = findFirstDueEntryAction(firstDiaryId, formsToDo.toDo, locale);
        return getJourneyPhaseCompleteScreen("profile", journey.nextStage, journeyCopy, {
            formButtonLabel: nextDueEntryAction?.buttonLabel,
        });
    }, [firstDiary, firstDiaryId, formsToDo.toDo, journey.nextStage, journeyCopy, locale]);

    const getNextRoute = (): ButtonConfig => {
        if (isPatient && patientJourneyScreen) {
            return patientJourneyScreen.primaryButton;
        }
        if (isPatient) {
            if (firstDiaryId) {
                return {
                    text: copy.addDoneSeeProgress,
                    to: "/diaries/$diaryId/home/to-do",
                    params: { diaryId: firstDiaryId },
                };
            }
            return { text: copy.goToDiary, to: "/diaries" };
        }
        return { text: copy.next, to: getOnboardedAppHome(auth.roles) };
    };

    const showPatientJourneySuccess = isPatient && patientJourneyScreen && !hasNoPatientDiaries && auth.roles?.hasRole;

    if (showPatientJourneySuccess) {
        return (
            <JourneyPhaseSuccessPage
                title={patientJourneyScreen.title}
                description={patientJourneyScreen.description}
                statusLabel={patientJourneyScreen.statusLabel}
                journeyProgress={journeyProgress}
                primaryButton={patientJourneyScreen.primaryButton}
            />
        );
    }

    const nextRoute = getNextRoute();
    const successTitle =
        hasNoPatientDiaries && diaryQuotaBlocked
            ? authCopy.diaryInviteUnavailableTitle
            : hasNoPatientDiaries
              ? diaryCopy.listEmptyTitle
              : auth.roles?.hasRole
                ? isPatient && patientJourneyScreen
                    ? patientJourneyScreen.title
                    : copy.onboardingComplete
                : copy.authenticationError;
    const successDescription =
        hasNoPatientDiaries && diaryQuotaBlocked
            ? `${authCopy.diaryInviteUnavailableBody} ${authCopy.diaryInviteUnavailableNextStep}`
            : hasNoPatientDiaries
              ? diaryCopy.listEmptyDescription
              : isPatient && patientJourneyScreen
                ? patientJourneyScreen.description
                : isPatient
                  ? copy.patientSetupCompleteDescription
                  : copy.setupCompleteDescription;

    return (
        <SuccessPage
            title={successTitle}
            description={auth.roles?.hasRole ? successDescription : copy.supportMessage}
            icon={auth.roles?.hasRole && !hasNoPatientDiaries ? Check : X}
            minLoadingTime={0}
            primaryButton={{
                text: nextRoute.text,
                to: nextRoute.to,
                params: nextRoute.params,
            }}
            secondaryButton={
                hasNoPatientDiaries && !diaryQuotaBlocked
                    ? {
                          text: diaryCopy.listCheckAgain,
                          onClick: () => void router.invalidate(),
                          showWhen: true,
                      }
                    : !auth.roles?.hasRole
                      ? {
                            text: copy.retry,
                            to: "#",
                            showWhen: true,
                        }
                      : undefined
            }
        />
    );
};

export const Route = createFileRoute("/onboarding/complete")({
    component: OnboardingComplete,
    loader: async ({ context }) => {
        const authId = context.auth.id;
        if (!authId) {
            return { isPatient: false, diaries: [] as StatefulDiaryT[], diaryQuotaBlocked: false };
        }

        const roles = await context.queryClient.ensureQueryData(usersOrganisationsRolesQueryOptions(authId));
        context.auth.setRoles(roles ?? []);

        const profile = await context.queryClient.ensureQueryData(selectProfileByIdQueryOptions(authId));
        if (profile) {
            context.auth.setIsSuperUser(profile.is_super_user ?? false);
        }

        const isPatient = roles?.some((role) => role.role?.toLowerCase() === "patient") ?? false;
        let diaries: StatefulDiaryT[] = [];
        let diaryQuotaBlocked = false;

        if (isPatient) {
            diaries = await context.queryClient.ensureQueryData(diariesByPatientQueryOptions(authId));
            if (diaries.length === 0) {
                const failedError = await fetchLatestFailedQuickstartError(authId);
                diaryQuotaBlocked = isDiaryQuotaQuickstartError(failedError);

                if (!diaryQuotaBlocked) {
                    await supabaseClient.functions.invoke("public-retry-quickstart").catch(() => undefined);
                    diaries = await pollPatientDiaries(context.queryClient, authId, {
                        maxAttempts: 3,
                        intervalMs: 1000,
                    });

                    if (diaries.length === 0) {
                        const afterRetryError = await fetchLatestFailedQuickstartError(authId);
                        diaryQuotaBlocked = isDiaryQuotaQuickstartError(afterRetryError);
                    }
                }
            }
        }

        return { isPatient, diaries, diaryQuotaBlocked };
    },
});
